import React from "react";
import ResizeDetector from "react-resize-detector";
import SearchStepper from "./components/SearchStepper/SearchStepper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Container, Modal, ModalBody } from "react-bootstrap";
import { Collapse } from "@mui/material";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import FilterGroupList from "./components/FilterGroup/FilterGroupList";
import { useSearch } from "./SearchProvider";
import Header from "../components/Header/Header";
import { faChevronDown, faChevronUp, faFilter, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import ResponsiveSearchForm from "./components/ResponsiveSearchForm";
import { useCustomer } from "../../customHooks/useCustomer";
import { faTag } from "@fortawesome/free-solid-svg-icons/faTag";
import BookingCouponAmount from "../components/BookingCouponAmount/BookingCouponAmount";
import '../components/CustomerMenu/CustomerMenu.module.scss';
import { useStyleContext } from "../../customHooks/useStyleContext";
import { useLocation } from "react-router-dom";
import MenuContainer from "../components/MenuContainer/MenuContainer";
import AmountDisplay from "../components/AmountDisplay/AmountDisplay";
import VehicleDefaultModel from "../components/VehicleDefaultModel/VehicleDefaultModel";
import classes from "./components/VehicleList/VehicleList.scss.json";
import PriceDisplay from "../../shared/components/PriceDisplay";

export const SearchLayout = ({ children }) => {
    const styleContext = useStyleContext();
    const { isLog, setOpenModalLogin } = useCustomer();
    const routerLocation = useLocation();
    const {
        stepHeader,
        filterGroups,
        backgroundColor,
        agencyList,
        searchParameters,
        handleSearchDateAndPlaceUpdate,
        loading,
        toggleSearchModal,
        selectedVehicle,
        bookingPreview,
        bookingPreviewCollapse,
        setBookingPreviewCollapse,
        bookingPreviewLoading,
        selectedFranchiseLevels,
        kilometersStage,
        couponData,
        tariffFrequencyText,
        objectFromQuery,
        toggleFilterModal,
        filterModalOpen,
        handleFilterSelect,
        selectedFilterGroups,
        objectFromQueryStr,
        cookiePriceHasChanged,
        setCookiePriceHasChanged
    } = useSearch();

    const subtitleMode = React.useMemo(() => {
        return selectedVehicle?.category?.subtitle_mode ?? "default"
    }, [selectedVehicle]);

    const subtitle = React.useMemo(() => {
        if (selectedVehicle?.category?.subtitle_mode === "enabled") {
            return selectedVehicle?.category?.custom_subtitle
        } else if (selectedVehicle?.category?.subtitle_mode === "disabled") {
            return ""
        }
        return "(ou similaire)"
    }, [selectedVehicle]);

    const kilometersIncludedText = React.useMemo(() => {
        if (!bookingPreview) return null;

        const stage = bookingPreview?.option_pack_amount?.kilometers_stage ?? kilometersStage ?? null;

        if (bookingPreview?.tariff_package) {
            if (bookingPreview.tariff_package.unlimited_kilometers) return "Illimité";
            const km = bookingPreview.tariff_package.kilometers;
            return typeof km === 'number' ? `${km} km` : (km ? `${km} km` : "—");
        }

        if (!stage) return null;
        if (stage.unlimited_kilometers) return "Illimité";

        const kmPerDay = stage.kilometers_per_day;
        if (typeof kmPerDay !== 'number' && !kmPerDay) return "—";

        if (stage.kilometer_type === "total") return `${kmPerDay} km`;

        return tariffFrequencyText ? `${kmPerDay} km/${tariffFrequencyText}` : `${kmPerDay} km`;
    }, [bookingPreview, kilometersStage, tariffFrequencyText]);

    React.useEffect(() => {
        if (filterGroups?.length > 0 && objectFromQueryStr !== "{}") {
            const filterGroupId = Number(objectFromQuery?.filter_group_id);

            handleFilterSelect(filterGroupId);
        }
    }, [filterGroups, objectFromQueryStr]);

    return (
        <>
            {/* Subdivide into small component (aka: SearchNavigation, LogoContainer, etc.) */}
            <Header
                styles={styleContext}
                stepHeader={stepHeader}
                headerAlignment={"justify-content-between"}
                rightComponent={(
                    <>
                        <div className={cx("w-100 d-lg-flex d-none", { "justify-content-end": !styleContext?.header?.configs?.["config-hide_logo_on_app"], "justify-content-center": styleContext?.header?.configs?.["config-hide_logo_on_app"] })}>
                            <ResizeDetector
                                handleWidth
                                render={({ width }) => {
                                    return (
                                        <div className={"w-75"}>
                                            <SearchStepper step={stepHeader} hideLabel={false} canHideLabel={false} />
                                        </div>
                                    )
                                }}
                            />
                            <MenuContainer hideLink={true} hideReturn={true} />
                        </div>
                        <div className="d-flex d-lg-none ml-auto">
                            <MenuContainer hideLink={true} hideReturn={true} />
                        </div>
                        {filterGroups?.length > 0 && (
                            <div>
                                <div className={"p-4 position-relative d-block d-lg-none"} onClick={toggleFilterModal}>
                                    <FontAwesomeIcon icon={faFilter} className={"c-grey fs30"} />
                                    {selectedFilterGroups?.length > 0 && (<span className={"bold c-white bg-main justify-content-center align-items-center d-flex h20 w20 rounded-circle"}
                                        style={{ position: 'absolute', top: '10px', right: '10px' }}
                                    >{selectedFilterGroups.length}</span>)}
                                </div>
                                <Modal
                                    show={filterModalOpen}
                                    fullscreen={true}
                                    contentClassName={"w-100"}
                                    centered
                                    animation={true}
                                >
                                    <ModalHeader className={cx('d-flex justify-content-between')}>
                                        <p className={"medium fs18"}>Filtres {selectedFilterGroups?.length > 0 && (<span className={'fst-italic bold c-main'}> - {selectedFilterGroups?.length}</span>)}</p>
                                        <Button onClick={toggleFilterModal}>Fermer</Button>
                                    </ModalHeader>
                                    <ModalBody>
                                        <FilterGroupList filterGroups={filterGroups} selectedFilterGroup={selectedFilterGroups} onSelect={handleFilterSelect} />
                                    </ModalBody>
                                </Modal>
                            </div>
                        )}
                    </>
                )}
            />
            <div className={cx("booking-module", { "bg-light-grey": !Boolean(backgroundColor) })} style={{
                paddingTop: '75px',
                height: "calc(var(--app-height))",
                overflowY: "scroll",
                overflowX: "hidden",
                backgroundColor: backgroundColor ?? "transparent"
            }}>
                <ResizeDetector
                    handleWidth
                    render={({ width }) => {
                        return (
                            <div className={cx("shadow-sm bg-white d-xl-none")}>
                            {/* <div className={cx("shadow-sm bg-white d-sm-none", { "sticky-top": routerLocation.pathname !== "/" })}> */}
                                <div className={"d-flex justify-content-around"}>
                                    <div className={"p-2 w-100"}>
                                        <div className={"d-flex justify-content-between"}>
                                            <p className={"medium"}>{routerLocation.pathname === "/" ? 'Votre recherche' : 'Récapitulatif de votre réservation'}</p>
                                        </div>
                                        <ResponsiveSearchForm
                                            editable={routerLocation.pathname === "/"}
                                            agencies={agencyList}
                                            params={objectFromQuery}
                                            searchParameters={searchParameters}
                                            onSearch={handleSearchDateAndPlaceUpdate}
                                            loading={loading}
                                            onInputClick={toggleSearchModal}
                                        />

                                        {selectedVehicle && (
                                            <>
                                                <hr className={"my-2"} />

                                                {cookiePriceHasChanged && (
                                                    <div className=' alert alert-warning w-100 d-flex flex-column w-100 p-2' role='alert'>
                                                        <button type='button' className='btn align-self-end m-0' onClick={() => setCookiePriceHasChanged(false)}>
                                                            <FontAwesomeIcon icon={faTimes} className='me-2' />
                                                        </button>
                                                        <p className='text-center'>Le prix de votre réservation a changé.</p>
                                                        <br />
                                                        <p className='text-center'>Veuillez vérifier les détails de votre réservation avant de continuer.</p>
                                                    </div>
                                                )}

                                                <div className="d-flex flex-row justify-content-between">
                                                    <div className={"d-flex flex-column flex-md-row justify-content-start align-items-center mb-2 flex-1"}>
                                                        <span className={"bold fs18"} style={{ marginRight: 3 }}><VehicleDefaultModel vehicle={selectedVehicle} /></span>
                                                        {subtitleMode !== 'disabled' && <span className={"fst-italic fs14"}>{subtitle}</span>}
                                                    </div>

                                                    {bookingPreview?.pricing_group && bookingPreview?.pricing_group.public_name && (
                                                        <div id={"group-" + bookingPreview?.pricing_group.id} className={cx(classes["pricing-group"])} style={{ width: "fit-content", margin: 0, marginBottom: "10px" }}>
                                                            <p className={"medium"}>Tarification client appliquée : </p>
                                                            <p className={classes.bolder}>{bookingPreview?.pricing_group.public_name}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={"d-flex justify-content-between pointer"} onClick={() => setBookingPreviewCollapse(prevState => !prevState)}>
                                                    <p>Montant Total :  <span className={"bold no-tr"}>{bookingPreviewLoading?.length ? "..." : <PriceDisplay amount={bookingPreview?.total_amount} />}</span></p>
                                                    <p className={"fst-italic"}><span className={"ms-4"}><FontAwesomeIcon icon={bookingPreviewCollapse ? faChevronUp : faChevronDown} /></span></p>
                                                </div>
                                                <Collapse in={bookingPreviewCollapse} >
                                                    <div className={"d-flex justify-content-between mt-3"}>
                                                        <p className={"regular"}>Durée</p>
                                                        <p className={"medium"}>{bookingPreview?.nb_days} jour{bookingPreview?.nb_days > 1 && 's'}</p>
                                                    </div>
                                                    {(selectedFranchiseLevels?.length > 0 || bookingPreview?.option_pack_amount?.franchise_level) && (
                                                        <>
                                                            {bookingPreview?.option_pack_amount?.franchise_level ? (
                                                                <>
                                                                    <div className={"d-flex justify-content-between mt-1"}>
                                                                        <p className={"regular"}>Montant de la franchise</p>
                                                                        {loading?.length ? <>...</> : (
                                                                            <p className={"bold"}><PriceDisplay amount={bookingPreview?.option_pack_amount?.franchise_level?.franchise_amount} /></p>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>

                                                                    {selectedFranchiseLevels?.map(franchiseLevel => {
                                                                        return <div
                                                                            key={`franchise-level-recap-mobile-${franchiseLevel?.id}`}>
                                                                            <div className={"d-flex justify-content-between mt-1"}>
                                                                                <p className={"regular"}>
                                                                                    {franchiseLevel?.franchise_level_type?.name ?
                                                                                        <span>{`Franchise ${franchiseLevel?.franchise_level_type?.name}`}</span>
                                                                                        :
                                                                                        <span>Montant de Franchise</span>
                                                                                    }
                                                                                </p>
                                                                                {loading?.length ? <>...</> : (
                                                                                    <p className={"bold"}><PriceDisplay amount={franchiseLevel?.franchise_amount} /></p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    })}
                                                                </>
                                                            )}
                                                        </>
                                                    )}

                                                    {bookingPreview?.deposit && (
                                                        <div className={"d-flex justify-content-between mt-1"}>
                                                            <p className={"regular"}>Montant de la caution</p>
                                                            {loading?.length ? <span>...</span> : (
                                                                <p className={classes.bolder}><PriceDisplay amount={bookingPreview?.deposit?.amount} /></p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {(kilometersStage || bookingPreview?.tariff_package?.kilometers || bookingPreview?.option_pack_amount?.kilometers_stage) &&
                                                        <div className={"d-flex justify-content-between mt-1"}>
                                                            <p className={"regular"}>Kilométrage inclus</p>
                                                            {loading?.length ? <>...</> : (
                                                                <p className={"bold"}>{kilometersIncludedText ?? "—"}</p>
                                                            )}
                                                        </div>
                                                    }

                                                    <div className={"d-flex justify-content-between mt-3"}>
                                                        <p className={"medium"}>Montant <span className={"fs12 fst-italic"}>(hors option)</span></p>
                                                        <p className={"bold"}>
                                                            <AmountDisplay
                                                                tariffDisplayMode={bookingPreview?.tariff_display_mode}
                                                                invoicingMode={bookingPreview?.invoicing_mode}
                                                                inclAmount={bookingPreview?.incl_amount}
                                                                exclAmount={bookingPreview?.excl_amount}
                                                                defaultAmount={bookingPreview?.amount}
                                                            />
                                                        </p>
                                                    </div>
                                                    {bookingPreview?.option_amount_details?.cdw_option ? <>
                                                        <div className={"d-flex justify-content-between mt-2"}>
                                                            <p className={"medium"}>{bookingPreview?.option_amount_details?.cdw_option?.option_name}</p>
                                                            {bookingPreviewLoading.length ? <>...</> : (
                                                                <p className={"bold"}>
                                                                    <PriceDisplay amount={bookingPreview?.option_amount_details?.cdw_option?.amount} />
                                                                </p>
                                                            )}
                                                        </div>
                                                    </> : <></>}

                                                    {selectedFranchiseLevels?.length > 0 && bookingPreview?.franchise_level_amount != null ? <>
                                                        {bookingPreview?.franchise_level_amount > 0 && (
                                                            <div className={"d-flex justify-content-between mt-1"}>
                                                                <p className={"regular"}>Rachat de franchise</p>
                                                                {loading?.length ? <>...</> : <p className={"bold"}><PriceDisplay amount={bookingPreview?.franchise_level_amount} /></p>}
                                                            </div>
                                                        )}
                                                    </> : <></>}

                                                    {bookingPreview?.option_pack_amount?.name ? <>
                                                        <div className={"d-flex justify-content-between mt-2"}>
                                                            <p className={"regular"}>{bookingPreview?.option_pack_amount?.name}</p>
                                                            {bookingPreviewLoading?.length ? <>...</> : <p className={"bold"}><PriceDisplay amount={bookingPreview?.option_pack_amount?.amount} /></p>}
                                                        </div>
                                                    </> : <></>}
                                                    {(bookingPreview?.option_pack_amount?.options?.length > 0 || bookingPreview?.option_amount_details?.options?.length) > 0 && (
                                                        <>
                                                            <p className={"mt-2 medium"}>Autres options</p>
                                                            <hr className={"my-2"} />
                                                            <div className={""}>
                                                                {bookingPreview?.option_pack_amount?.options?.map((option) => {
                                                                    return <div key={`option-pack-recap-${option.option_id}`} className={"d-flex justify-content-between"}>
                                                                        <p className={"regular"}>{option.option_name}</p>
                                                                        {bookingPreviewLoading.length ? <>...</> : <p className={"light fst-italic c-main"}>Inclus</p>}
                                                                    </div>
                                                                })}
                                                                {bookingPreview?.option_amount_details?.options?.length ?
                                                                    <>
                                                                        {bookingPreview?.option_amount_details?.options?.map((option) => {
                                                                            return <div key={`option-recap-${option.option_id}`} className={"d-flex justify-content-between"}>
                                                                                <p className={"regular"}>{option.option_name} {option.quantity > 1 && `(x${option.quantity})`}</p>
                                                                                {bookingPreviewLoading.length ? <>...</> : <p className={"bold"}><PriceDisplay amount={option.amount} /></p>}
                                                                            </div>
                                                                        })}
                                                                    </> :
                                                                    <></>
                                                                }
                                                                {(!bookingPreview?.option_pack_amount?.options.length && !bookingPreview?.option_amount_details?.options?.length) && <>
                                                                    <p className={"fst-italic c-grey"}>Aucune option</p>
                                                                </>}

                                                            </div>
                                                        </>
                                                    )}
                                                    {couponData?.code && <>
                                                        <div className={"d-flex justify-content-between mt-2 border bradius-5 p-2"}>
                                                            <div className={"d-flex align-items-center"}>
                                                                <FontAwesomeIcon className={"me-2"} icon={faTag} />
                                                                <p><span
                                                                    className={"medium"}>{couponData?.code ?? couponData?.name} {couponData.type === "percentage" && <>(-{parseInt(couponData?.value)} %)</>}</span>
                                                                </p>
                                                            </div>
                                                            {bookingPreviewLoading?.length ? <>...</> :
                                                                <BookingCouponAmount
                                                                    className={"regular fst-italic"}
                                                                    couponAmount={bookingPreview?.coupon_excl_amount ?? bookingPreview?.coupon_amount}
                                                                    couponType={couponData?.type}
                                                                    invoicingMode={bookingPreview?.invoicing_mode}
                                                                    vatRate={bookingPreview?.vat_rate}
                                                                    currencySymbol={styleContext?.currency?.symbol}
                                                                />
                                                            }
                                                        </div>
                                                    </>}
                                                    {styleContext?.app_config?.booking_conditions?.length > 0 && (
                                                        <>
                                                            <hr />
                                                            <div className={"bg-white p-2 mt-3"}>
                                                                <p className={"medium fs14 underline mb-1"}>Conditions de
                                                                    réservation :</p>
                                                                <div className={"bg-white p-2"}>
                                                                    {styleContext?.app_config?.booking_conditions?.map((conditionItem, index) => {
                                                                        return <p key={`booking-condition-text-${index}`} className={"fs14"}>- {conditionItem}</p>
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </Collapse>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    }}
                />
                
                <div className={cx({ "bg-light-grey": !Boolean(backgroundColor) })} >
                    <Container>
                        {children}
                    </Container>
                </div>
            </div>
        </>
    );
};